import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import HereMap from '@rio-cloud/rio-uikit/Map';
import type { MapApi } from '@rio-cloud/rio-uikit/mapTypes';
import Route from '@rio-cloud/rio-uikit/Route';
import { type ReactNode, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { config } from '../../../config.ts';
import type { TourStop } from './TourStop.ts';
import { TourStopMarkers, type TourStopMarkersHandle } from './TourStopMarkers.tsx';
import { TourStopsEmptyState } from './TourStopsEmptyState.tsx';
import { getTourStopMapBounds, getTourStopMapItems, getTourStopMapSegments } from './tourStopsMap.utils.ts';

type TourStopsMapProps = {
    stops: TourStop[];
    height?: number;
};

const ROUTE_STYLE = { color: '#0784e3', width: 4, borderWidth: 0 };

const PreviewMessage = ({ children }: { children: ReactNode }) => (
    <div className='flex-1-1 display-grid place-items-center min-height-250 bg-lightest rounded text-color-dark'>
        <span className='padding-20 text-center'>{children}</span>
    </div>
);

export const TourStopsMap = ({ stops, height = 400 }: TourStopsMapProps) => {
    const intl = useIntl();
    const markersRef = useRef<TourStopMarkersHandle>(null);
    const mapModel = useMemo(() => {
        const items = getTourStopMapItems(stops);
        return {
            items,
            segments: getTourStopMapSegments(items),
            bounds: getTourStopMapBounds(items),
        };
    }, [stops]);
    const mapEventListenerMap = useMemo(
        () => ({
            [EventUtils.MAP_VIEW_CHANGE_START]: () => markersRef.current?.resetForMapMovement(),
            [EventUtils.MAP_VIEW_CHANGE_END]: () => markersRef.current?.recalculate(),
        }),
        []
    );
    const missingStopCount = stops.length - mapModel.items.length;
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.title',
        defaultMessage: 'Route preview',
    });

    return (
        <section className='flex-shrink-0 display-flex flex-column gap-10' aria-label={title}>
            <h4 className='margin-0'>{title}</h4>
            {stops.length === 0 ? (
                <TourStopsEmptyState outerClassName='border-none flex-1-1 margin-0 bg-lightest rounded' />
            ) : mapModel.items.length === 0 ? (
                <PreviewMessage>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.no-coordinates',
                        defaultMessage: 'No selected stops have valid coordinates.',
                    })}
                </PreviewMessage>
            ) : !config.hereApiKey ? (
                <PreviewMessage>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.unavailable',
                        defaultMessage: 'The map preview is unavailable because HERE is not configured.',
                    })}
                </PreviewMessage>
            ) : (
                <>
                    {missingStopCount > 0 && (
                        <output className='alert alert-warning margin-0 display-block'>
                            {intl.formatMessage(
                                {
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.omitted',
                                    defaultMessage: 'Hidden stops: {count}. Coordinates are missing or invalid.',
                                },
                                { count: missingStopCount }
                            )}
                        </output>
                    )}
                    <div className='position-relative overflow-hidden border rounded shadow-subtle min-height-250'>
                        <HereMap
                            credentials={{ apikey: config.hereApiKey }}
                            boundingBox={mapModel.bounds}
                            height={height}
                            showCluster={false}
                            eventListenerMap={mapEventListenerMap}
                        >
                            {(api: MapApi) => (
                                <>
                                    {mapModel.segments.map(segment => (
                                        <Route
                                            key={segment.id}
                                            positions={[segment.from, segment.to]}
                                            hasArrows
                                            style={ROUTE_STYLE}
                                        />
                                    ))}
                                    <TourStopMarkers ref={markersRef} api={api} items={mapModel.items} />
                                </>
                            )}
                        </HereMap>
                    </div>
                </>
            )}
        </section>
    );
};
